import { FormEvent, useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  createEngagement,
  createEntity,
  listEngagements,
  listEntities,
  listOrganisations,
  myAuthority,
  setLifecycle,
} from "../lib/api";
import type {
  AssessedEntity,
  Engagement,
  EntityKind,
  MembershipRole,
  Organisation,
} from "../lib/types";
import { ENTITY_KIND_LABEL } from "../lib/labels";
import { useBreadcrumb } from "../shell/ContextHeader";

// One client organisation: its assessed entities (portfolio / program /
// project) and the engagements against them. Member-writable per RLS; the
// archive control is rendered only for owners because app.protect_tenant_row()
// would refuse anyone else - the button's absence mirrors the database's
// answer rather than inventing a client-side permission model.

type State =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | {
      kind: "ready";
      org: Organisation;
      role: MembershipRole | null;
      entities: AssessedEntity[];
      engagements: Engagement[];
    };

export function ClientDetail() {
  const { orgId } = useParams<{ orgId: string }>();
  const { setTrail } = useBreadcrumb();
  const [state, setState] = useState<State>({ kind: "loading" });
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!orgId) return;
    try {
      const [orgs, role, entities, engagements] = await Promise.all([
        listOrganisations(),
        myAuthority(orgId),
        listEntities(orgId),
        listEngagements(orgId),
      ]);
      const org = orgs.find((o) => o.id === orgId);
      if (!org) {
        setState({ kind: "error", message: "organisation not found, or you are not a member" });
        return;
      }
      setState({ kind: "ready", org, role, entities, engagements });
      setTrail([
        { label: "Clients & engagements", to: "/clients" },
        { label: org.name },
      ]);
    } catch (e) {
      setState({ kind: "error", message: (e as Error).message });
    }
  }, [orgId, setTrail]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.kind === "loading") return <p>Loading…</p>;
  if (state.kind === "error")
    return <div className="error-banner">Could not load: {state.message}</div>;

  const { org, role, entities, engagements } = state;
  const isOwner = role === "owner";
  const canWrite = role === "owner" || role === "lead_assessor" || role === "assessor";

  async function archive(table: "assessed_entities" | "engagements", id: string, status: "active" | "archived") {
    setActionError(null);
    try {
      await setLifecycle(table, id, status);
      await load();
    } catch (e) {
      setActionError((e as Error).message);
    }
  }

  return (
    <>
      <h1>{org.name}</h1>
      {actionError && <div className="error-banner">{actionError}</div>}

      <section style={{ marginBottom: "1.5rem" }}>
        <h2>Assessed entities</h2>
        <EntityTable
          entities={entities}
          isOwner={isOwner}
          onLifecycle={(id, status) => archive("assessed_entities", id, status)}
        />
        {canWrite && <NewEntityForm orgId={org.id} entities={entities} onCreated={load} onError={setActionError} />}
      </section>

      <section>
        <h2>Engagements</h2>
        <EngagementTable
          engagements={engagements}
          entities={entities}
          isOwner={isOwner}
          onLifecycle={(id, status) => archive("engagements", id, status)}
        />
        {canWrite && entities.filter((e) => e.status === "active").length > 0 && (
          <NewEngagementForm orgId={org.id} entities={entities} onCreated={load} onError={setActionError} />
        )}
      </section>
    </>
  );
}

// ---- Entities ----

function EntityTable({
  entities,
  isOwner,
  onLifecycle,
}: {
  entities: AssessedEntity[];
  isOwner: boolean;
  onLifecycle: (id: string, status: "active" | "archived") => void;
}) {
  if (entities.length === 0) {
    return (
      <div className="card empty-state">
        <h3>No assessed entities yet</h3>
        <p>
          An assessed entity is the portfolio, program, project or PMO function
          an assessment runs against. Create the first one below.
        </p>
      </div>
    );
  }
  return (
    <div className="card" style={{ padding: 0 }}>
      <table className="table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Kind</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {entities.map((e) => (
            <tr key={e.id}>
              <td>{e.name}</td>
              <td>{ENTITY_KIND_LABEL[e.kind]}</td>
              <td>
                <span className={`badge badge-${e.status}`}>{e.status}</span>
              </td>
              <td style={{ textAlign: "right" }}>
                {isOwner && (
                  <button
                    className="btn btn-secondary"
                    onClick={() => onLifecycle(e.id, e.status === "active" ? "archived" : "active")}
                  >
                    {e.status === "active" ? "Archive" : "Restore"}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NewEntityForm({
  orgId,
  entities,
  onCreated,
  onError,
}: {
  orgId: string;
  entities: AssessedEntity[];
  onCreated: () => void;
  onError: (m: string) => void;
}) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState<EntityKind>("project");
  const [parentId, setParentId] = useState("");
  const [busy, setBusy] = useState(false);

  // A project may sit under a program, a program under a portfolio (0001 §4).
  const parentKind: EntityKind | null =
    kind === "project" ? "program" : kind === "program" ? "portfolio" : null;
  const parentOptions = entities.filter(
    (e) => e.status === "active" && parentKind !== null && e.kind === parentKind,
  );

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await createEntity({
        organisation_id: orgId,
        kind,
        name: name.trim(),
        ...(parentId ? { parent_id: parentId } : {}),
      });
      setName("");
      setParentId("");
      onCreated();
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card" style={{ marginTop: "0.75rem", display: "flex", gap: "0.75rem", alignItems: "flex-end", flexWrap: "wrap" }} onSubmit={submit}>
      <div className="field" style={{ marginBottom: 0, flex: "1 1 180px" }}>
        <label htmlFor="entity-name">New entity</label>
        <input id="entity-name" required value={name} placeholder="Name" onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="field" style={{ marginBottom: 0 }}>
        <label htmlFor="entity-kind">Kind</label>
        <select id="entity-kind" value={kind} onChange={(e) => { setKind(e.target.value as EntityKind); setParentId(""); }}>
          <option value="project">Project</option>
          <option value="program">Program</option>
          <option value="portfolio">Portfolio</option>
          <option value="pmo_function">PMO function</option>
        </select>
      </div>
      {parentKind && parentOptions.length > 0 && (
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="entity-parent">Under {ENTITY_KIND_LABEL[parentKind]} (optional)</label>
          <select id="entity-parent" value={parentId} onChange={(e) => setParentId(e.target.value)}>
            <option value="">—</option>
            {parentOptions.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
      )}
      <button className="btn" type="submit" disabled={busy || name.trim() === ""}>
        Create
      </button>
    </form>
  );
}

// ---- Engagements ----

function EngagementTable({
  engagements,
  entities,
  isOwner,
  onLifecycle,
}: {
  engagements: Engagement[];
  entities: AssessedEntity[];
  isOwner: boolean;
  onLifecycle: (id: string, status: "active" | "archived") => void;
}) {
  const entityName = new Map(entities.map((e) => [e.id, e.name]));
  if (engagements.length === 0) {
    return (
      <div className="card empty-state">
        <h3>No engagements yet</h3>
        <p>
          An engagement is a piece of consulting work against one assessed
          entity. Assessment runs hang off an engagement.
        </p>
      </div>
    );
  }
  return (
    <div className="card" style={{ padding: 0 }}>
      <table className="table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Entity</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {engagements.map((g) => (
            <tr key={g.id}>
              <td>
                <Link to={`/clients/${g.organisation_id}/engagements/${g.id}`}>{g.name}</Link>
              </td>
              <td>{entityName.get(g.entity_id) ?? "—"}</td>
              <td>
                <span className={`badge badge-${g.status}`}>{g.status}</span>
              </td>
              <td style={{ textAlign: "right" }}>
                {isOwner && (
                  <button
                    className="btn btn-secondary"
                    onClick={() => onLifecycle(g.id, g.status === "active" ? "archived" : "active")}
                  >
                    {g.status === "active" ? "Archive" : "Restore"}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NewEngagementForm({
  orgId,
  entities,
  onCreated,
  onError,
}: {
  orgId: string;
  entities: AssessedEntity[];
  onCreated: () => void;
  onError: (m: string) => void;
}) {
  const [name, setName] = useState("");
  const [entityId, setEntityId] = useState("");
  const [busy, setBusy] = useState(false);
  const active = entities.filter((e) => e.status === "active");

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await createEngagement({ organisation_id: orgId, entity_id: entityId, name: name.trim() });
      setName("");
      setEntityId("");
      onCreated();
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card" style={{ marginTop: "0.75rem", display: "flex", gap: "0.75rem", alignItems: "flex-end", flexWrap: "wrap" }} onSubmit={submit}>
      <div className="field" style={{ marginBottom: 0, flex: "1 1 180px" }}>
        <label htmlFor="eng-name">New engagement</label>
        <input id="eng-name" required value={name} placeholder="Name" onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="field" style={{ marginBottom: 0 }}>
        <label htmlFor="eng-entity">Entity</label>
        <select id="eng-entity" required value={entityId} onChange={(e) => setEntityId(e.target.value)}>
          <option value="">Choose…</option>
          {active.map((e) => (
            <option key={e.id} value={e.id}>{e.name} ({ENTITY_KIND_LABEL[e.kind]})</option>
          ))}
        </select>
      </div>
      <button className="btn" type="submit" disabled={busy || name.trim() === "" || entityId === ""}>
        Create
      </button>
    </form>
  );
}
